/**
 * Durable, read-only whole-board replay.
 *
 * Node-level verification history can answer “what did this source check say?”
 * but cannot restore a claim that was later pruned, a brief that was rewritten,
 * or the rest of the board around that event. A knowledge revision therefore
 * records the complete semantic state while deliberately excluding layout,
 * spend, and this history itself. The exclusion in `SemanticResearch` prevents
 * recursive snapshots.
 *
 * WHAT A ROW HOLDS. The first retained row carries a whole board; every later
 * row carries what changed since the retained row before it, and a reader
 * rebuilds any board by walking forward from the first. Sixty-four whole copies
 * weighed sixty-four boards — on disk, in memory, and through the packer on every
 * save — so a checked exploration's document was ninety-seven parts ledger to
 * three parts exploration (D81). A rebuilt board shares every unchanged record
 * with the board before it by reference, so the ledger costs about one board
 * plus what changed, however many states it retains, and a change is detected by
 * comparing canonical text, never by a hash.
 *
 * Rows are never mutated after they are made. That is what lets a rebuilt board
 * be memoised by row identity, and what lets a row survive an append untouched
 * when the row before it is still the row before it.
 */

import {
  canonicalSemanticJson,
  semanticSnapshot,
  type SemanticActor,
  type SemanticNode,
  type SemanticProfile,
  type SemanticResearch,
  type SemanticSnapshot,
} from './semanticGit'
import type { Bridge, Session, VerificationArchiveEntry } from './types'

export const KNOWLEDGE_REVISION_VERSION = 1 as const
export const MAX_KNOWLEDGE_REVISIONS = 64
/** A portable input may contain an unreduced pair of history windows. Decode
 * that finite predecessor chain before rebasing its newest window; never accept
 * unlimited replay work from a hand-edited document. Live writers retain 64. */
export const MAX_KNOWLEDGE_INPUT_REVISIONS = 2 * MAX_KNOWLEDGE_REVISIONS

/** A keyed section's changes: the record for an entry that changed or arrived,
 *  null for one that left. */
export type KnowledgeEntryChanges<T> = Record<string, T | null>

/**
 * What changed between one retained board and the next.
 *
 * A section is present only when it changed. `research` may be present AND
 * null, because a board can carry no research state and that is a value; a
 * section that is absent is unchanged, and the two are told apart by the key.
 */
export interface KnowledgeDelta {
  /** The retained row this applies to — always the one before it. */
  base: string
  profile?: SemanticProfile
  research?: SemanticResearch | null
  nodes?: KnowledgeEntryChanges<SemanticNode>
  bridges?: KnowledgeEntryChanges<Bridge>
  verificationArchive?: KnowledgeEntryChanges<VerificationArchiveEntry>
}

export interface KnowledgeRevision {
  version: typeof KNOWLEDGE_REVISION_VERSION
  id: string
  recordedAt: number
  reason: string
  actor: SemanticActor
  /** Operation/event id when one exists; never inferred from timestamps. */
  eventId?: string
  /** The whole board: on the first retained row, and on every row written before
   *  D81, which a reader still takes as it is. */
  snapshot?: SemanticSnapshot
  /** What changed since the retained row before this one. Exactly one of this
   *  and `snapshot` is present. */
  delta?: KnowledgeDelta
}

/** A retained state with its whole board in hand — what every reader is given.
 *  Also a valid row, so a captured board can be appended or stored as one. */
export interface KnowledgeBoard extends Omit<KnowledgeRevision, 'snapshot' | 'delta'> {
  snapshot: SemanticSnapshot
}

export interface KnowledgeHistory {
  revisions: KnowledgeRevision[]
  /** Exact count evicted by the explicit bounded-history policy. */
  omittedBefore: number
}

export interface CaptureKnowledgeRevisionInput {
  id: string
  recordedAt?: number
  reason: string
  actor?: SemanticActor
  eventId?: string
}

const lexical = (left: string, right: string): number => (left < right ? -1 : left > right ? 1 : 0)

const revisionOrder = (
  left: { recordedAt: number; id: string },
  right: { recordedAt: number; id: string },
): number => left.recordedAt - right.recordedAt || lexical(left.id, right.id)

/* ── sameness ─────────────────────────────────────────────────────────────
   One canonical serialisation per object, by identity. A record shared between
   boards is serialised once however many diffs read it, and the text is the
   comparison: two records are the same when their canonical JSON is the same,
   and nothing shorter stands in for it. What this holds in memory is about one
   board's JSON, for the records the newest board still shares. */

const canon = new WeakMap<object, string>()

function canonical(value: object): string {
  let held = canon.get(value)
  if (held === undefined) {
    held = canonicalSemanticJson(value)
    canon.set(value, held)
  }
  return held
}

const isObject = (value: unknown): value is object => typeof value === 'object' && value !== null

const same = (left: unknown, right: unknown): boolean =>
  left === right || (isObject(left) && isObject(right) && canonical(left) === canonical(right))

function sameRecord<T>(left: Record<string, T>, right: Record<string, T>): boolean {
  const keys = Object.keys(left)
  if (keys.length !== Object.keys(right).length) return false
  return keys.every((key) => key in right && same(left[key], right[key]))
}

/** Whether two boards mean the same thing. Cheap where they share records. */
export function sameKnowledgeBoard(left: SemanticSnapshot, right: SemanticSnapshot): boolean {
  return (
    left === right ||
    (same(left.profile, right.profile) &&
      same(left.research, right.research) &&
      sameRecord(left.nodes, right.nodes) &&
      sameRecord(left.bridges, right.bridges) &&
      sameRecord(left.verificationArchive, right.verificationArchive))
  )
}

/* ── the chain ────────────────────────────────────────────────────────── */

/** Boards rebuilt from rows, by row identity. */
const rebuilt = new WeakMap<KnowledgeRevision, SemanticSnapshot>()

function diffRecord<T extends object>(
  prev: Record<string, T>,
  next: Record<string, T>,
): { changes: KnowledgeEntryChanges<T> | undefined; shared: Record<string, T> } {
  const changes: KnowledgeEntryChanges<T> = {}
  const shared: Record<string, T> = {}
  let changed = false
  for (const key of Object.keys(next)) {
    const before = prev[key]
    const after = next[key]!
    if (before !== undefined && same(before, after)) {
      shared[key] = before
    } else {
      shared[key] = after
      changes[key] = after
      changed = true
    }
  }
  for (const key of Object.keys(prev)) {
    if (!(key in next)) {
      changes[key] = null
      changed = true
    }
  }
  return { changes: changed ? changes : undefined, shared }
}

/**
 * What `next` changed since `prev`, and `next` rewritten to share every
 * unchanged record with `prev` — the version a rebuilt board holds, so sharing
 * runs the length of the chain rather than one link of it.
 */
function diff(
  prev: SemanticSnapshot,
  next: SemanticSnapshot,
  base: string,
): { delta: KnowledgeDelta; shared: SemanticSnapshot } {
  const delta: KnowledgeDelta = { base }
  const profile = same(prev.profile, next.profile) ? prev.profile : next.profile
  if (profile !== prev.profile) delta.profile = next.profile
  const research = same(prev.research, next.research) ? prev.research : next.research
  if (research !== prev.research) delta.research = next.research
  const nodes = diffRecord(prev.nodes, next.nodes)
  if (nodes.changes) delta.nodes = nodes.changes
  const bridges = diffRecord(prev.bridges, next.bridges)
  if (bridges.changes) delta.bridges = bridges.changes
  const archive = diffRecord(prev.verificationArchive, next.verificationArchive)
  if (archive.changes) delta.verificationArchive = archive.changes
  return {
    delta,
    shared: {
      version: next.version,
      profile,
      research,
      nodes: nodes.shared,
      bridges: bridges.shared,
      verificationArchive: archive.shared,
    },
  }
}

function applyRecord<T>(
  prev: Record<string, T>,
  changes: KnowledgeEntryChanges<T> | undefined,
): Record<string, T> {
  if (!changes) return prev
  const out: Record<string, T> = { ...prev }
  for (const [key, value] of Object.entries(changes)) {
    if (value === null) delete out[key]
    else out[key] = value
  }
  return out
}

/** The board a delta makes of the one before it. Unchanged records are the
 *  same objects. */
export function applyKnowledgeDelta(
  prev: SemanticSnapshot,
  delta: KnowledgeDelta,
): SemanticSnapshot {
  return {
    version: prev.version,
    profile: delta.profile ?? prev.profile,
    research: 'research' in delta ? (delta.research ?? null) : prev.research,
    nodes: applyRecord(prev.nodes, delta.nodes),
    bridges: applyRecord(prev.bridges, delta.bridges),
    verificationArchive: applyRecord(prev.verificationArchive, delta.verificationArchive),
  }
}

const meta = (row: KnowledgeRevision | KnowledgeBoard): Omit<KnowledgeBoard, 'snapshot'> => ({
  version: KNOWLEDGE_REVISION_VERSION,
  id: row.id,
  recordedAt: row.recordedAt,
  reason: row.reason,
  actor: row.actor,
  ...(row.eventId ? { eventId: row.eventId } : {}),
})

export interface ResolvedKnowledgeRows {
  boards: KnowledgeBoard[]
  /** Rows that could not be rebuilt: a delta whose base is not the row before
   *  it. Every row chained on one is here too, because nothing can be rebuilt
   *  past a break. */
  dropped: KnowledgeRevision[]
}

/**
 * Every board the rows describe, in row order.
 *
 * A row carrying a whole board is taken as it is. A row carrying a delta is
 * rebuilt from the board before it, and only from that one: a delta names its
 * base, and a base that is not the row before it is a chain this reader will
 * not guess across. Memoised by row identity, so a walk is paid once per row.
 */
export function resolveKnowledgeRows(rows: readonly KnowledgeRevision[]): ResolvedKnowledgeRows {
  const boards: KnowledgeBoard[] = []
  const dropped: KnowledgeRevision[] = []
  let prev: { id: string; snapshot: SemanticSnapshot } | null = null
  for (const row of rows) {
    const chained =
      row.snapshot !== undefined || (row.delta !== undefined && row.delta.base === prev?.id)
    if (!chained) {
      dropped.push(row)
      // A break: the rows after this one chain on a board that is not here.
      prev = null
      continue
    }
    let snapshot = rebuilt.get(row)
    if (!snapshot) {
      snapshot = row.snapshot ?? applyKnowledgeDelta(prev!.snapshot, row.delta!)
      rebuilt.set(row, snapshot)
    }
    boards.push({ ...meta(row), snapshot })
    prev = { id: row.id, snapshot }
  }
  return { boards, dropped }
}

/** The boards, for a chain that is known to be whole — every in-memory one. */
export const knowledgeBoards = (rows: readonly KnowledgeRevision[]): KnowledgeBoard[] =>
  resolveKnowledgeRows(rows).boards

/**
 * Rows for boards in retained order: the first whole, every later one a delta
 * against the row before it. A row already held for a board is kept as it is
 * when the row before it is still the same row — the same OBJECT, not the same
 * id, because an id can be appended again with a different board under it and
 * a delta against the old one would then rebuild the wrong board. So an append
 * rewrites the rows it changes and no other. Whole rows past the first, which a
 * document from before D81 carries, become deltas here.
 */
function encodeKnowledgeRows(
  boards: readonly KnowledgeBoard[],
  held: readonly KnowledgeRevision[],
): KnowledgeRevision[] {
  const byId = new Map(held.map((row) => [row.id, row]))
  const before = new Map(held.map((row, index) => [row, held[index - 1] ?? null]))
  const rows: KnowledgeRevision[] = []
  let prev: { row: KnowledgeRevision; snapshot: SemanticSnapshot } | null = null
  for (const board of boards) {
    const have = byId.get(board.id)
    let row: KnowledgeRevision
    let snapshot: SemanticSnapshot
    if (!prev) {
      if (have?.snapshot && !have.delta && rebuilt.get(have) === board.snapshot) {
        row = have
      } else {
        row = { ...meta(board), snapshot: board.snapshot }
      }
      snapshot = board.snapshot
    } else if (
      have?.delta &&
      !have.snapshot &&
      before.get(have) === prev.row &&
      rebuilt.get(have) === board.snapshot
    ) {
      // Still the board it was, still after the row it followed.
      row = have
      snapshot = board.snapshot
    } else {
      const made = diff(prev.snapshot, board.snapshot, prev.row.id)
      row = { ...meta(board), delta: made.delta }
      snapshot = made.shared
    }
    rebuilt.set(row, snapshot)
    rows.push(row)
    prev = { row, snapshot }
  }
  return rows
}

/**
 * A history from boards: sorted, bounded, and encoded as a chain. The one place
 * rows are made, for the append and for the codec alike. `held` is the rows the
 * boards came from, in their order, so a row that still follows the row it
 * followed before is kept rather than remade.
 */
export function normalizeKnowledgeHistory(
  boards: readonly KnowledgeBoard[],
  omittedBefore: number,
  held: readonly KnowledgeRevision[] = [],
  maximum = MAX_KNOWLEDGE_REVISIONS,
): KnowledgeHistory {
  const limit = Math.max(1, Math.floor(maximum))
  const sorted = [...boards].sort(revisionOrder)
  const omitted = Math.max(0, sorted.length - limit)
  return {
    revisions: encodeKnowledgeRows(sorted.slice(-limit), held),
    omittedBefore: Math.max(0, omittedBefore) + omitted,
  }
}

/** Capture only meaning. The returned record cannot mutate with the live graph. */
export function captureKnowledgeRevision(
  session: Session,
  input: CaptureKnowledgeRevisionInput,
): KnowledgeBoard {
  return {
    version: KNOWLEDGE_REVISION_VERSION,
    id: input.id,
    recordedAt: input.recordedAt ?? Date.now(),
    reason: input.reason.trim() || 'Research state changed',
    actor: input.actor ?? { kind: 'system', id: 'pathways-history' },
    snapshot: semanticSnapshot(session),
    ...(input.eventId?.trim() ? { eventId: input.eventId.trim() } : {}),
  }
}

/**
 * Append one distinct semantic state. Exact duplicate boards are coalesced,
 * while the newer label/time replaces the older row so the visible event is the
 * action that actually settled. The cap is explicit and counted, never silent.
 */
export function appendKnowledgeRevision(
  history: KnowledgeHistory,
  revision: KnowledgeBoard,
  maximum = MAX_KNOWLEDGE_REVISIONS,
): KnowledgeHistory {
  const byId = new Map(knowledgeBoards(history.revisions).map((board) => [board.id, board]))
  byId.set(revision.id, revision)
  const distinct = [...byId.values()].sort(revisionOrder)
  const at = distinct.findIndex((board) => board.id === revision.id)
  const prior = distinct[at - 1]
  const next = distinct[at + 1]
  if (next && sameKnowledgeBoard(next.snapshot, revision.snapshot)) {
    // The later event is the durable label for one unchanged board.
    distinct.splice(at, 1)
  } else if (prior && sameKnowledgeBoard(prior.snapshot, revision.snapshot)) {
    distinct.splice(at - 1, 1)
  }
  return normalizeKnowledgeHistory(distinct, history.omittedBefore, history.revisions, maximum)
}

/** Latest recorded whole-board state at or before the requested wall-clock date. */
export function knowledgeRevisionAsKnown(
  revisions: readonly KnowledgeRevision[],
  at: number,
): KnowledgeBoard | null {
  if (!Number.isFinite(at)) return null
  return (
    knowledgeBoards(revisions)
      .filter((board) => board.recordedAt <= at)
      .sort(revisionOrder)
      .at(-1) ?? null
  )
}

export interface KnowledgeRevisionSummary {
  claims: number
  checkedClaims: number
  outcomes: number
  refutedClaims: number
  branches: number
}

/** A deterministic whole-board summary for the read-only replay surface. */
export function summarizeKnowledgeRevision(revision: KnowledgeBoard): KnowledgeRevisionSummary {
  const nodes = Object.values(revision.snapshot.nodes)
  return {
    claims: nodes.length,
    checkedClaims: nodes.filter((node) => node.evidence.verification !== undefined).length,
    outcomes: nodes.filter((node) => node.evidence.outcome !== undefined).length,
    refutedClaims: nodes.filter((node) => node.evidence.verification?.reading === 'refuted').length,
    branches: Object.keys(revision.snapshot.bridges).length,
  }
}
